import { ChevronDown, ExternalLink, Laptop, Loader2, Monitor, MoreHorizontal, Plus, RotateCw, Trash2, Unplug, X } from "lucide-react"
import { type ReactNode, useState } from "react"
import type { PairedDevice } from "../../computers/access"
import type { ComputerAccessSummary, ComputerSummary } from "../../computers/types"
import { directNestOrigin } from "../../nest/routes"
import { openExternalUrl } from "../../platform/actions"
import { Switch } from "../ui/Switch"
import { RevealOnMount } from "./RevealOnMount"

export type ReachabilityTone = "checking" | "on" | "pending" | "off"

export interface Reachability {
    tone: ReachabilityTone
    label: string
}

export function reachabilityFor(state: { enabled: boolean; active: boolean } | null): Reachability {
    if (!state) return { tone: "checking", label: "Checking…" }
    if (state.active && state.enabled) return { tone: "on", label: "Reachable from anywhere" }
    if (state.active) return { tone: "pending", label: "Reachable until the next restart" }
    if (state.enabled) return { tone: "pending", label: "Reachable after the next restart" }
    return { tone: "off", label: "Only on this computer" }
}

const DOT: Record<ReachabilityTone, string> = {
    checking: "bg-base-300",
    on: "bg-success",
    pending: "bg-warning",
    off: "bg-base-300",
}

function pairedAt(value: string): string {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "Pair date unavailable"
    return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

function CardShell({
    icon,
    name,
    tag,
    status,
    tone,
    trailing,
    children,
}: {
    icon: ReactNode
    name: string
    tag?: string
    status: string
    tone: ReachabilityTone
    trailing?: ReactNode
    children?: ReactNode
}) {
    return (
        <div className="flex flex-col border border-border bg-base-200" data-computer-card={name}>
            <div className="flex items-start gap-3 p-4">
                <div className="flex h-10 w-10 flex-none items-center justify-center bg-base-100 text-base-content">{icon}</div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-base-content">{name}</span>
                        {tag && <span className="flex-none text-[10px] font-medium uppercase tracking-[0.12em] text-muted">{tag}</span>}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted">
                        <span className={`h-1.5 w-1.5 flex-none ${DOT[tone]}`} aria-hidden="true" />
                        <span className="truncate">{status}</span>
                    </div>
                </div>
                {trailing}
            </div>
            {children}
        </div>
    )
}

export function LocalComputerCard({
    computer,
    reachability,
    remoteSwitch,
    pendingRestart,
    restarting,
    onRestart,
    devices,
    devicesBusy,
    onRevokeDevice,
    revokingTokenId,
    confirmTokenId,
    canAddDevice,
    addDeviceBusy,
    addDeviceOpen,
    onAddDevice,
}: {
    computer: ComputerSummary
    reachability: Reachability
    remoteSwitch: { checked: boolean; disabled: boolean; onChange(enabled: boolean): void } | null
    pendingRestart: boolean
    restarting: boolean
    onRestart(): void
    devices: PairedDevice[]
    devicesBusy: boolean
    onRevokeDevice(device: PairedDevice): void
    revokingTokenId: string | null
    confirmTokenId: string | null
    canAddDevice: boolean
    addDeviceBusy: boolean
    addDeviceOpen: boolean
    onAddDevice(): void
}) {
    const others = devices.filter((device) => !device.current)
    return (
        <CardShell icon={<Laptop className="h-4 w-4" />} name={computer.name} tag="This Mac" status={reachability.label} tone={reachability.tone}>
            {remoteSwitch && (
                <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
                    <div className="min-w-0">
                        <div className="text-sm text-base-content">Reachable from anywhere</div>
                        <div className="text-xs leading-5 text-muted">Your other devices and colleagues connect through the private relay.</div>
                    </div>
                    {reachability.tone === "checking" ? (
                        <Loader2 className="h-4 w-4 flex-none animate-spin text-muted" />
                    ) : (
                        <Switch
                            checked={remoteSwitch.checked}
                            onCheckedChange={remoteSwitch.onChange}
                            aria-label="Reachable from anywhere"
                            disabled={remoteSwitch.disabled}
                            className={{ root: "h-6 w-11 flex-none rounded-full", thumb: "h-5 w-5 rounded-full" }}
                        />
                    )}
                </div>
            )}
            {pendingRestart && (
                <div className="flex items-center justify-between gap-3 border-t border-border bg-warning/10 px-4 py-3">
                    <p className="text-xs leading-5 text-base-content">
                        {remoteSwitch?.checked ? "Turns on after the next restart." : "Turns off after the next restart."}
                    </p>
                    <button
                        type="button"
                        className="btn flex h-8 flex-none items-center gap-1.5 bg-primary px-3 text-xs font-medium text-primary-content hover:bg-primary/90"
                        disabled={restarting}
                        onClick={onRestart}
                    >
                        {restarting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                        Restart now
                    </button>
                </div>
            )}
            {canAddDevice && (
                <details className="group border-t border-border">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm text-base-content hover:bg-base-300/40 [&::-webkit-details-marker]:hidden">
                        <span className="flex items-center gap-2">
                            Devices
                            <span className="text-xs text-muted">
                                {devicesBusy && devices.length === 0
                                    ? "…"
                                    : others.length === 0
                                      ? "Only this one"
                                      : `${others.length} other${others.length === 1 ? "" : "s"}`}
                            </span>
                        </span>
                        <ChevronDown className="h-3.5 w-3.5 text-muted transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="divide-y divide-border border-t border-border bg-base-100">
                        {others.length === 0 ? (
                            <div className="px-4 py-3 text-xs text-muted">No other devices yet. Add your phone or another computer below.</div>
                        ) : (
                            others.map((device) => (
                                <div key={device.tokenId} className="flex items-center gap-3 px-4 py-2.5">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm text-base-content">{device.displayName}</div>
                                        <div className="text-[11px] text-muted">Paired {pairedAt(device.createdAt)}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className={`btn flex h-8 items-center gap-1.5 px-2.5 text-xs ${
                                            confirmTokenId === device.tokenId ? "bg-error text-error-content" : "text-muted hover:bg-error/10 hover:text-error"
                                        }`}
                                        onClick={() => onRevokeDevice(device)}
                                        disabled={revokingTokenId !== null}
                                    >
                                        {revokingTokenId === device.tokenId ? (
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        ) : (
                                            <Unplug className="h-3.5 w-3.5" />
                                        )}
                                        {confirmTokenId === device.tokenId ? "Confirm" : "Revoke"}
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </details>
            )}
            {canAddDevice && (
                <button
                    type="button"
                    className={`btn flex h-11 w-full items-center justify-center gap-2 border-t border-border text-sm font-medium ${
                        addDeviceOpen ? "bg-primary/5 text-primary" : "text-primary hover:bg-base-300/40"
                    }`}
                    onClick={onAddDevice}
                    disabled={addDeviceBusy}
                    aria-expanded={addDeviceOpen}
                >
                    {addDeviceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Add a device
                </button>
            )}
        </CardShell>
    )
}

function accessLabel(access: ComputerAccessSummary | undefined): Reachability {
    if (!access) return { tone: "off", label: "Added by link" }
    if (access.provider === "relay") {
        if (access.status === "ready") return { tone: "on", label: "Private relay" }
        if (access.status === "checking") return { tone: "checking", label: "Checking…" }
        return { tone: "pending", label: access.detail ?? `Private relay · ${access.status}` }
    }
    return { tone: access.status === "ready" ? "on" : "pending", label: access.networkName ?? "Private network" }
}

export function RemoteComputerCard({ computer, onRemove }: { computer: ComputerSummary; onRemove(): void }) {
    const [open, setOpen] = useState(false)
    const [confirming, setConfirming] = useState(false)
    const origin = directNestOrigin(computer.routes)
    const reachability = accessLabel(computer.access)
    const openInBrowser = async () => {
        if (!origin) return
        const result = await openExternalUrl(origin)
        if (result.status !== "ok") throw new Error(result.status === "failed" ? result.message : result.reason)
    }
    return (
        <CardShell
            icon={<Monitor className="h-4 w-4" />}
            name={computer.name}
            status={reachability.label}
            tone={reachability.tone}
            trailing={
                <button
                    type="button"
                    className={`btn flex h-8 w-8 flex-none items-center justify-center ${
                        open ? "bg-base-300/60 text-base-content" : "text-muted hover:bg-base-300/60 hover:text-base-content"
                    }`}
                    aria-label={`Options for ${computer.name}`}
                    aria-expanded={open}
                    onClick={() => {
                        setConfirming(false)
                        setOpen((value) => !value)
                    }}
                >
                    {open ? <X className="h-4 w-4" /> : <MoreHorizontal className="h-4 w-4" />}
                </button>
            }
        >
            {open && (
                <RevealOnMount className="flex flex-wrap items-center gap-1 border-t border-border bg-base-100 px-3 py-2">
                    {confirming ? (
                        <>
                            <span className="px-1.5 text-xs text-muted">Remove {computer.name} from this app? Nothing changes on that computer.</span>
                            <span className="flex-1" />
                            <button
                                type="button"
                                className="btn flex h-8 items-center justify-center bg-error px-3 text-xs font-medium text-error-content"
                                onClick={onRemove}
                            >
                                Remove
                            </button>
                            <button
                                type="button"
                                className="btn flex h-8 items-center justify-center px-3 text-xs text-muted hover:text-base-content"
                                onClick={() => setConfirming(false)}
                            >
                                Keep
                            </button>
                        </>
                    ) : (
                        <>
                            {origin && (
                                <button
                                    type="button"
                                    className="btn flex h-8 items-center gap-1.5 px-2.5 text-xs font-medium text-base-content hover:bg-base-300/60"
                                    onClick={() => void openInBrowser()}
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    Open in browser
                                </button>
                            )}
                            <span className="flex-1" />
                            <button
                                type="button"
                                className="btn flex h-8 items-center gap-1.5 px-2.5 text-xs font-medium text-muted hover:bg-error/10 hover:text-error"
                                onClick={() => setConfirming(true)}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                Remove
                            </button>
                        </>
                    )}
                </RevealOnMount>
            )}
        </CardShell>
    )
}
